import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/utils"

// Shared by every text-entry control and trigger (input, input group, select,
// native select, combobox) so they line up side by side in a filter bar.
const inputVariants = cva(
  [
    "flex w-full min-w-0 items-center rounded-md border border-input bg-card text-foreground focus-ring outline-none",
    "transition-[color,background-color,border-color] duration-fast ease-standard",
    "placeholder:text-muted-foreground",
    "aria-invalid:border-destructive aria-invalid:focus-visible:outline-destructive",
    "disabled:cursor-not-allowed disabled:border-border disabled:bg-muted disabled:text-muted-foreground",
  ],
  {
    variants: {
      size: {
        sm: "h-control-sm px-2 text-ui",
        md: "h-control-md px-2.5 text-ui",
        lg: "h-control-lg px-3 text-body-sm",
        default: "h-control-md px-2.5 text-ui",
      },
    },
    defaultVariants: {
      size: "md",
    },
  },
)

type InputProps = Omit<React.ComponentProps<"input">, "size"> &
  VariantProps<typeof inputVariants>

function Input({ className, type, size = "md", ...props }: InputProps) {
  return (
    <input
      type={type}
      data-slot="input"
      data-size={size}
      className={cn(
        inputVariants({ size }),
        "tabular-nums selection:bg-primary selection:text-primary-foreground read-only:bg-muted",
        "file:mr-2 file:inline-flex file:h-full file:border-0 file:bg-transparent file:text-ui file:font-medium file:text-foreground",
        "[&::-webkit-search-cancel-button]:appearance-none",
        className,
      )}
      {...props}
    />
  )
}

export { Input, inputVariants }
export type { InputProps }
